import { useEffect, useRef, useState } from "react";
import Drawer from "./Drawer";
import Icon from "./Icon";
import { showApiError, showToast } from "../utils/toast";

/**
 * Generic three-bucket bulk-import drawer. Consumers pass:
 *   - `downloadFn()`        - returns a promise; called when the user clicks
 *                              "Download sample template / current data".
 *   - `importFn(file)`      - returns the axios response from POSTing the file.
 *   - `instructions` (str)  - line shown above the download button.
 *   - `title`, `subtitle`   - drawer header text.
 *
 * The response is expected to be:
 *   { message, data: { imported, skipped, failed } }
 * where each `imported` row optionally carries `was_updated: bool` so we can
 * label it Created vs Updated.
 */
export default function BulkImportDrawer({
  open,
  onClose,
  onImported,
  title = "Bulk import",
  subtitle,
  instructions = "Download the current data, edit in Excel, then upload the file.",
  downloadLabel = "Download sample template",
  downloadFn,
  importFn,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setResult(null);
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const handleDownload = async () => {
    try { await downloadFn(); } catch (err) { showApiError(err); }
  };

  const handleImport = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      // axios.js installs a response interceptor that unwraps to the body,
      // so `body` here is already `{status, message, data: {imported,...}}`.
      const body = await importFn(file);
      const bundle = body?.data || { imported: [], skipped: [], failed: [] };
      setResult(bundle);
      const importedCount = bundle.imported?.length || 0;
      if (importedCount > 0) {
        showToast("success", body?.message || `${importedCount} rows imported`);
        onImported?.();
      } else {
        showToast("info", body?.message || "No rows were imported");
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      wide
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {result ? "Close" : "Cancel"}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={!file || busy}
          >
            {busy ? "Importing…" : "Import"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Step 1 — Get the template</label>
          <button className="btn btn-secondary" type="button" onClick={handleDownload}>
            <Icon name="download" size={14} /> {downloadLabel}
          </button>
          <div className="field-hint">{instructions}</div>
        </div>

        <div className="field">
          <label className="field-label">Step 2 — Upload the filled file</label>
          <input
            ref={fileInputRef}
            className="input"
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setResult(null);
            }}
          />
          {file && (
            <div className="field-hint">
              Selected: <strong>{file.name}</strong> ({Math.round(file.size / 1024)} KB)
            </div>
          )}
        </div>

        {result && (
          <div className="col gap-sm">
            <ResultBucket
              title="Imported"
              count={result.imported?.length || 0}
              variant="success"
              rows={result.imported}
              renderRow={(r) => (
                <span>
                  Row {r.row}
                  {r.was_updated !== undefined && (
                    <span
                      className={`badge ${r.was_updated ? "badge-info" : "badge-success"}`}
                      style={{ marginLeft: 6 }}
                    >
                      {r.was_updated ? "updated" : "created"}
                    </span>
                  )}
                  {(r.client_id || r.name) && (
                    <>
                      {" · "}
                      <span className="t-mono">{r.client_id || ""}</span>
                      {r.client_id && r.name ? " — " : ""}
                      {r.name || r.client_name || ""}
                    </>
                  )}
                </span>
              )}
            />
            <ResultBucket
              title="Skipped"
              count={result.skipped?.length || 0}
              variant="warning"
              rows={result.skipped}
              renderRow={(r) => (
                <span>
                  Row {r.row}
                  {(r.client_id || r.identifier) && (
                    <>
                      {" · "}
                      <span className="t-mono">{r.client_id || r.identifier}</span>
                    </>
                  )}
                  {r.reason ? ` — ${r.reason}` : ""}
                </span>
              )}
            />
            <ResultBucket
              title="Failed"
              count={result.failed?.length || 0}
              variant="danger"
              rows={result.failed}
              renderRow={(r) => (
                <div>
                  <div>
                    Row {r.row}
                    {(r.client_id || r.identifier) && (
                      <>
                        {" · "}
                        <span className="t-mono">{r.client_id || r.identifier}</span>
                      </>
                    )}
                  </div>
                  <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                    {Object.entries(r.errors || {}).flatMap(([field, msgs]) =>
                      (Array.isArray(msgs) ? msgs : [msgs]).map((m, i) => (
                        <li key={`${field}-${i}`} className="t-muted text-xs">
                          <strong>{field}:</strong> {m}
                        </li>
                      )),
                    )}
                  </ul>
                </div>
              )}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
}

function ResultBucket({ title, count, variant, rows, renderRow }) {
  const [open, setOpen] = useState(count > 0 && variant !== "success");
  const badgeClass =
    variant === "success" ? "badge-success" :
    variant === "warning" ? "badge-warning" :
    variant === "danger"  ? "badge-danger"  : "badge-neutral";
  return (
    <div className="card" style={{ padding: 0 }}>
      <button
        type="button"
        className="row"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", padding: "8px 12px", background: "none",
          border: 0, cursor: count ? "pointer" : "default", textAlign: "left",
        }}
        disabled={!count}
      >
        <Icon name={open ? "chevron-down" : "chevron-right"} size={12} />
        <strong style={{ marginLeft: 6 }}>{title}</strong>
        <span className={`badge ${badgeClass}`} style={{ marginLeft: "auto" }}>{count}</span>
      </button>
      {open && count > 0 && (
        <ul style={{ margin: 0, padding: "0 12px 10px 28px", listStyle: "disc" }}>
          {rows.map((r, i) => (
            <li key={i} className="text-sm" style={{ marginTop: 4 }}>
              {renderRow(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
